/* Shared map core (rename + generalisation of kns-mt-map.js). Boots once per
   .kns-map root when it nears the viewport, then pulls in MapLibre. Everything
   page-specific (the points, the strings, the fallback link, whether chips
   render) arrives in the JSON config the snippet kns-map.liquid renders; this
   file holds no page text and no page logic. It owns: lazy MapLibre loading and
   the inline dark style, stars, dim secondary dots and clusters, the docked
   card (single or list mode), chips (per point or per group), cooperative
   gestures, the MapLibre locale and the outage fallback, and frame.knsMap. One
   shared look in three modes: dining (the Maker's Table hub), makers
   (/pages/brands) and one maker (the product-page modal, mode 'open', which
   loads nothing at all until the pill opens it).
   The point schema: {name, sub, eyebrow_place, date_iso, url, link_label, img,
   img_alt, logo, lat, lng, group, secondary, links:[{label,url}]}. date_iso
   formats with Intl in UTC (en-AU day order) through the i18n `visited`
   pattern; with no date the eyebrow is only eyebrow_place; link_label defaults
   to the i18n read_story. `group` rides the group chips (chips: 'groups'),
   `secondary` paints a 6px dim grey dot in its own source (no glow, no
   clusters, labelled on hover, a tap swaps the card), and `links` renders
   several links on one card. Config options: points (inline), pointsUrl
   (lazy JSON fetch merged in at boot; a failed fetch keeps the given points),
   maxZoom, focusMaxZoom, initialZoom and initialCenter (with a given center
   there is no intro fly), chips, mode ('root' | 'open'), card ('docked' |
   'below'), mapUrl, sub_lang, i18n.
   Camera rules: the fit bounds skip points outside Japan (Hong Kong sits off
   the overview); the fit ceiling is maxZoom, so a modal capped at 13 never
   implies a street address from town-level coordinates; focusMaxZoom (opt-in,
   brands) caps ONLY the programmatic focus moves (the group-chip fit, a
   chip/row/card/star tap through focusPlace), never the map's own maxZoom —
   the overview fit or a cluster-click expansion.
   History kept from the Maker's Table rounds: the palette follows
   DESIGN-DECISION.md 1 (in OpenMapTiles the background IS the land; the water
   polygons are the sea); the card is a panel docked INSIDE the frame, opened at
   once; every UI string comes from the i18n object in the config (the English
   literals below are only the fallback); the MapLibre `locale` overrides ride in
   i18n.maplibre (merged over MapLibre's own English defaults); and the outage
   fallback: before the first `load` a source/tile error or a load timeout shows
   the quiet failure line once and neutralises the chips. */

const ML_URL = 'https://cdn.jsdelivr.net/npm/maplibre-gl@6.12.0/dist/maplibre-gl.mjs';
const ML_CSS = 'https://cdn.jsdelivr.net/npm/maplibre-gl@6.12.0/dist/maplibre-gl.css';
const GOLD = '#e0b700';
const GOLD_BRIGHT = '#ffd24d';
const GREY_DOT = '#8e8f96';
const GREY_BRIGHT = '#d3d4da';
const LAND = '#2c2d31';
const OVERVIEW = [[128.9, 33.2], [140.6, 36.4]];
const JAPAN = [[129, 31], [145, 43]];
const MAX_BOUNDS = [[118, 22], [156, 48]];
const CITY_WHITELIST = [
  'Tokyo', 'Osaka', 'Kyoto', 'Nagoya', 'Fukuoka', 'Sapporo', 'Sendai',
  'Hiroshima', 'Kanazawa', 'Toyama', 'Fukui',
];
const EN_LABEL = ['coalesce', ['get', 'name:en'], ['get', 'name:latin'], ['get', 'name']];
const LABEL_FONT = ['Noto Sans Regular'];

// English fallback strings only: a page without an i18n object still reads well.
// Every page is expected to pass its own strings in the config.
const STR_DEFAULT = {
  all_places: 'All places',
  all_groups: 'All',
  read_story: 'Read the story',
  visited: 'Visited {date}',
  close: 'Close',
  map_label: 'Map of the places',
  fallback_intro: 'The interactive map could not load here. ',
  fallback_link: 'Open the list of the places in Google Maps',
  fallback_side: 'The list of the places is linked beside the map.',
  makers_of: '{n} makers',
  back: 'Back to makers',
  chip_group: '{label} ({n})',
  list_label: 'Jump to a maker',
  disclosure: 'Where our makers work · {n} workshops',
};

// Stars paint at a 48 CSS px base (96 px canvas at pixelRatio 2): 20 px at z5,
// 24 px at z13, and about 28 px once selected.
const STAR_BASE = 48;

function inBounds(b, lng, lat) {
  return lng >= b[0][0] && lng <= b[1][0] && lat >= b[0][1] && lat <= b[1][1];
}

let cssPromise = null;
function loadCss() {
  if (!cssPromise) {
    cssPromise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = ML_CSS;
      link.onload = () => resolve();
      link.onerror = () => reject(new Error('maplibre css'));
      document.head.appendChild(link);
    });
  }
  return cssPromise;
}

function starCanvas(size) {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  const outer = (size / 2) * 0.9;
  const inner = outer * 0.45;
  const cx = size / 2;
  const cy = size / 2;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * 2 * i) / 10 - Math.PI / 2;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = '#fff';
  ctx.fill();
  return ctx.getImageData(0, 0, size, size); // addImage takes pixel data, not a canvas
}

// number_decimal may arrive as a JSON string; anything else (null, '') is not a coordinate.
const num = (v) => (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '') ? Number(v) : NaN);

// The shared point sanitiser: config.points and a pointsUrl payload both pass
// through here, so a fetched point can never be shaped differently from an
// inline one. A usable point has finite coords and a root-relative url (that
// is what a card link can honestly be).
function cleanPoints(arr, cap) {
  if (!Array.isArray(arr)) return null;
  const pts = arr
    .filter((p) => p && Number.isFinite(num(p.lat)) && Number.isFinite(num(p.lng))
      && typeof p.url === 'string' && p.url.charAt(0) === '/' && p.url.charAt(1) !== '/')
    .slice(0, cap)
    .map((p, i) => ({
      // The index IS the pid: features carry it through promoteId 'pid', and a
      // hit feature resolves back through places[id].
      id: i,
      // Shopify's json filter renders an empty metafield as a bare null.
      name: typeof p.name === 'string' && p.name ? p.name : 'Untitled',
      sub: typeof p.sub === 'string' ? p.sub : '',
      eyebrow_place: typeof p.eyebrow_place === 'string' ? p.eyebrow_place : '',
      date_iso: typeof p.date_iso === 'string' ? p.date_iso : '',
      link_label: typeof p.link_label === 'string' && p.link_label ? p.link_label : '',
      url: p.url,
      img: typeof p.img === 'string' && (/^(https?:)?\/\//).test(p.img) ? p.img : '',
      img_alt: typeof p.img_alt === 'string' ? p.img_alt : '',
      logo: typeof p.logo === 'string' && (/^(https?:)?\/\//).test(p.logo) ? p.logo : '',
      group: typeof p.group === 'string' ? p.group : '',
      secondary: p.secondary === true || p.secondary === 'true',
      links: Array.isArray(p.links) ? p.links
        .filter((l) => l && typeof l.url === 'string' && l.url.charAt(0) === '/' && l.url.charAt(1) !== '/'
          && typeof l.label === 'string' && l.label)
        .slice(0, 4)
        // shop: the link lands on THIS page (a ?maker= filter), so it scrolls
        // to the grid instead of paying a reload when the target is at page end.
        .map((l) => (l.shop === true || l.shop === 'true' ? { label: l.label, url: l.url, shop: true } : { label: l.label, url: l.url })) : [],
      lng: Math.max(-180, Math.min(180, num(p.lng))),
      lat: Math.max(-90, Math.min(90, num(p.lat))),
    }));
  if (!pts.length) return null;
  return pts;
}

/* Reads the JSON config block the snippet renders inside the root:
   {points: [...], pointsUrl, i18n: {...}, chips: 'points' | 'groups' | 'none',
   mode: 'root' | 'open', card: 'below', maxZoom, focusMaxZoom, initialZoom,
   initialCenter, mapUrl, sub_lang}.
   Returns null when the block is missing or unsalvageable. */
function readConfig(root) {
  let raw = null;
  try {
    const node = root.querySelector('script[data-kns-map-config]');
    const parsed = JSON.parse(node.textContent);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) raw = parsed;
  } catch (err) { /* treated as no config below */ }
  if (!raw) return null;
  const str = Object.assign({}, STR_DEFAULT);
  let mlLocale = null;
  if (raw.i18n && typeof raw.i18n === 'object' && !Array.isArray(raw.i18n)) {
    for (const key in raw.i18n) {
      if (typeof raw.i18n[key] === 'string') str[key] = raw.i18n[key];
    }
    if (raw.i18n.maplibre && typeof raw.i18n.maplibre === 'object' && !Array.isArray(raw.i18n.maplibre)) {
      mlLocale = raw.i18n.maplibre;
    }
  }
  return {
    points: cleanPoints(raw.points, 100),
    pointsUrl: typeof raw.pointsUrl === 'string' && (/^\/[^/]/).test(raw.pointsUrl) ? raw.pointsUrl : '',
    // The focal point's url: with it set, fetched points join as grey dots.
    secondaryFrom: typeof raw.secondaryFrom === 'string' ? raw.secondaryFrom : '',
    str,
    mlLocale,
    chips: raw.chips === 'none' ? 'none' : (raw.chips === 'groups' ? 'groups' : 'points'),
    mode: raw.mode === 'open' ? 'open' : 'root',
    // 'below' (brands): the card may mount AFTER the frame while the frame is
    // in its bottom-sheet layout. Anything else: docked inside, as everywhere.
    cardBelow: raw.card === 'below',
    mapUrl: typeof raw.mapUrl === 'string' ? raw.mapUrl : '',
    subLang: typeof raw.sub_lang === 'string' && raw.sub_lang ? raw.sub_lang : 'ja',
    maxZoom: Number.isFinite(num(raw.maxZoom)) ? num(raw.maxZoom) : null,
    // Opt-in cap on the programmatic focus moves only (see the camera rules).
    focusMaxZoom: Number.isFinite(num(raw.focusMaxZoom)) ? num(raw.focusMaxZoom) : null,
    initialZoom: Number.isFinite(num(raw.initialZoom)) ? num(raw.initialZoom) : null,
    initialCenter: Array.isArray(raw.initialCenter)
      && Number.isFinite(num(raw.initialCenter[0])) && Number.isFinite(num(raw.initialCenter[1]))
      ? [Math.max(-180, Math.min(180, num(raw.initialCenter[0]))), Math.max(-90, Math.min(90, num(raw.initialCenter[1])))]
      : null,
  };
}

function visitedText(str, iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  // The ISO date parses as UTC midnight; timeZone: 'UTC' keeps the day from
  // shifting for viewers behind UTC (the I18N-zh-TW date edge).
  const d = new Date(iso + 'T00:00:00Z');
  // A bare 'en' (the AU storefront) would format US-style; the site writes '15 August 2026'.
  const lang = document.documentElement.lang || 'en-AU';
  const date = new Intl.DateTimeFormat(/^en(-US)?$/i.test(lang) ? 'en-AU' : lang,
    { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(d);
  return str.visited.replace('{date}', date);
}

// Idempotent: the outage watcher may call it from two paths (an error before
// the first load, or the load timeout), and boot's own catch runs alongside.
function showFailure(root, cfg) {
  if (root.classList.contains('is-map-failed')) return;
  const str = (cfg && cfg.str) || STR_DEFAULT;
  const panel = root.querySelector('.kns-map__frame');
  if (!panel) return;
  panel.textContent = '';
  const p = document.createElement('p');
  p.className = 'kns-map__fallback';
  p.appendChild(document.createTextNode(str.fallback_intro));
  const url = cfg && cfg.mapUrl;
  if (url && url.indexOf('http') === 0) {
    const a = document.createElement('a');
    a.href = url;
    a.textContent = str.fallback_link;
    p.appendChild(a);
  } else {
    p.appendChild(document.createTextNode(str.fallback_side));
  }
  panel.appendChild(p);
  root.classList.add('is-map-failed');
  // Neutralise anything bound to the map: the chips had no effect without it.
  root.querySelectorAll('.kns-map__chips button').forEach((btn) => {
    btn.disabled = true;
    btn.tabIndex = -1;
    btn.setAttribute('aria-disabled', 'true');
  });
}

function buildStyle() {
  const labelBase = {
    type: 'symbol',
    source: 'omt',
    'source-layer': 'place',
    layout: {
      'text-field': EN_LABEL,
      'text-font': LABEL_FONT,
      'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 13, 13.5],
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.06,
      'text-max-width': 7,
      // Labels step aside from the stars (design decision 2).
      'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
      'text-radial-offset': 0.8,
      'text-padding': 6,
    },
    paint: {
      'text-color': '#9a9ba1',
      'text-halo-color': LAND, // the land colour, since the background IS the land
      'text-halo-width': 1.6,
      'text-opacity': 0.9,
    },
  };
  const label = (extra) => Object.assign({}, labelBase, extra, {
    layout: Object.assign({}, labelBase.layout, (extra && extra.layout) || {}),
    paint: Object.assign({}, labelBase.paint, (extra && extra.paint) || {}),
  });
  const road = (extra) => Object.assign({
    type: 'line',
    source: 'omt',
    'source-layer': 'transportation',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {},
  }, extra);
  return {
    version: 8,
    glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    sources: {
      omt: { type: 'vector', url: 'https://tiles.openfreemap.org/planet' },
    },
    layers: [
      // The background is the land: OpenMapTiles emits no landuse polygons
      // classed 'land', so the old dedicated land layer never painted anything.
      { id: 'bg', type: 'background', paint: { 'background-color': LAND } },
      {
        id: 'land-water',
        type: 'fill',
        source: 'omt',
        'source-layer': 'water',
        paint: { 'fill-color': '#1b1c21' },
      },
      {
        id: 'coast',
        type: 'line',
        source: 'omt',
        'source-layer': 'water',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#45474e',
          'line-opacity': 0.7,
          'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 12, 1.2],
        },
      },
      {
        id: 'boundary-pref',
        type: 'line',
        source: 'omt',
        'source-layer': 'boundary',
        filter: ['==', ['get', 'admin_level'], 4],
        minzoom: 6,
        layout: { 'line-cap': 'butt', 'line-join': 'miter' },
        paint: {
          'line-color': '#3b3c42',
          'line-opacity': 0.35,
          'line-dasharray': [3, 4],
          'line-width': ['interpolate', ['exponential', 1.3], ['zoom'], 6, 0.5, 14, 1.6],
        },
      },
      road({
        id: 'road-motorway',
        minzoom: 7,
        filter: ['==', ['get', 'class'], 'motorway'],
        paint: {
          'line-color': '#3a3b40',
          'line-opacity': 0.5,
          'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 7, 0.5, 13, 2.2, 20, 12],
        },
      }),
      road({
        id: 'road-trunk',
        minzoom: 7,
        filter: ['==', ['get', 'class'], 'trunk'],
        paint: {
          'line-color': '#36373d',
          'line-opacity': 0.45,
          'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 7, 0.4, 13, 1.6, 20, 8],
        },
      }),
      road({
        id: 'road-minor',
        minzoom: 12,
        filter: ['match', ['get', 'class'], ['minor', 'service', 'track'], true, false],
        paint: {
          'line-color': '#33343a',
          'line-opacity': 0.5,
          'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 12, 0.6, 18, 3],
        },
      }),
      road({
        id: 'railway',
        minzoom: 11,
        filter: ['==', ['get', 'class'], 'rail'],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: {
          'line-color': '#3d3e45',
          'line-opacity': 0.35,
          'line-dasharray': [2, 2],
          'line-width': ['interpolate', ['exponential', 1.3], ['zoom'], 11, 0.4, 17, 1.8],
        },
      }),
      {
        id: 'building',
        type: 'fill',
        source: 'omt',
        'source-layer': 'building',
        minzoom: 14,
        paint: {
          'fill-color': '#313237',
          'fill-opacity': 0.6,
          'fill-outline-color': '#3c3e44',
        },
      },
      label({
        id: 'label-country',
        maxzoom: 7,
        filter: ['all',
          ['==', ['get', 'class'], 'country'],
          ['match', ['get', 'name:en'], ['Japan'], true, false],
        ],
        layout: { 'text-size': 13.5, 'text-letter-spacing': 0.22 },
        paint: { 'text-color': '#909198', 'text-opacity': 0.85 },
      }),
      label({
        id: 'label-city-far',
        maxzoom: 7,
        filter: ['all',
          ['==', ['get', 'class'], 'city'],
          ['in', ['get', 'name:en'], ['literal', CITY_WHITELIST]],
        ],
      }),
      label({
        id: 'label-city',
        minzoom: 7,
        maxzoom: 11,
        filter: ['==', ['get', 'class'], 'city'],
      }),
      label({
        id: 'label-city-near',
        minzoom: 11,
        filter: ['all',
          ['==', ['get', 'class'], 'city'],
          ['<=', ['get', 'rank'], 3],
        ],
      }),
      label({
        id: 'label-town',
        minzoom: 7,
        filter: ['==', ['get', 'class'], 'town'],
        paint: { 'text-opacity': 0.8 },
      }),
      label({
        id: 'label-village',
        minzoom: 11,
        filter: ['match', ['get', 'class'], ['village', 'suburb'], true, false],
        paint: { 'text-opacity': 0.7 },
      }),
    ],
  };
}

function init(root, maplibregl, cfg) {
  const frame = root.querySelector('.kns-map__frame');
  const chipsBox = root.querySelector('.kns-map__chips');
  const places = cfg.points;
  if (!frame) return false;
  // The config announces the map region in the page language.
  if (cfg.str.map_label) root.setAttribute('aria-label', cfg.str.map_label);
  if (chipsBox && cfg.str.list_label) chipsBox.setAttribute('aria-label', cfg.str.list_label);
  if (!places) { showFailure(root, cfg); return true; }

  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let hasWebgl = false;
  try {
    hasWebgl = !!document.createElement('canvas').getContext('webgl');
  } catch (err) { hasWebgl = false; }
  if (!hasWebgl) { showFailure(root, cfg); return true; }

  // Secondary points (the pop-up's neighbours) live in their own unclustered
  // source and never enter the primary bookkeeping: no chips, no overview, no
  // gold ring. The oracles count gold stars + clusters only.
  const primary = places.filter((p) => !p.secondary);
  if (!primary.length) { showFailure(root, cfg); return true; }
  const secondary = places.filter((p) => p.secondary);
  const groupsOn = cfg.chips === 'groups';
  const ceiling = cfg.maxZoom != null ? cfg.maxZoom : 17;
  const clusterMaxZoom = cfg.maxZoom != null ? Math.min(11, cfg.maxZoom - 2) : 11;
  // The star a click or hover resolves through lives in one of two sources.
  const srcOf = (p) => (p && p.secondary ? 'kns-map-secondary' : 'kns-map-points');
  // The camera box must always contain a point a chip can fly to: the default
  // Japan box (with a generous ring) grows to include any point parked outside
  // it (a Hong Kong maker sits west of lng 118). Every point counts, fetched
  // secondary dots included (the merge in bootRoot has run before init): the
  // pop-up's inline set is the single focal star, and its grey neighbours must
  // all be pannable to.
  const mbW = Math.min(118, ...places.map((p) => p.lng));
  const mbS = Math.min(22, ...places.map((p) => p.lat));
  const mbE = Math.max(156, ...places.map((p) => p.lng));
  const mbN = Math.max(48, ...places.map((p) => p.lat));

  // Chips are built from the points (the engine, not the page, so a filtered
  // point list can never leave a stale chip behind). Group chips: "All" first,
  // then one per group with its count; a group chip fits its points (allowing
  // for the docked card) AND opens the list card; a group of one opens that
  // point's card. Chips never filter the product grid.
  if (groupsOn && chipsBox) {
    const groupNames = [];
    places.forEach((p) => { if (p.group && !p.secondary && groupNames.indexOf(p.group) < 0) groupNames.push(p.group); });
    const allBtn = document.createElement('button');
    allBtn.type = 'button';
    allBtn.className = 'kns-map__chip';
    allBtn.dataset.knsMapGroup = '';
    allBtn.textContent = cfg.str.all_groups;
    chipsBox.appendChild(allBtn);
    groupNames.forEach((g) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'kns-map__chip';
      b.dataset.knsMapGroup = g;
      let n = 0;
      primary.forEach((p) => { if (p.group === g) n++; });
      b.textContent = cfg.str.chip_group.replace('{label}', g).replace('{n}', String(n));
      chipsBox.appendChild(b);
    });
  }
  if (!groupsOn && cfg.chips === 'points' && chipsBox) {
    primary.forEach((p) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'kns-map__chip';
      b.dataset.knsMapPid = String(p.id);
      b.textContent = p.name;
      chipsBox.appendChild(b);
    });
  }

  // The camera fit skips points outside Japan so an outlier (Hong Kong) never
  // shrinks the Japan overview; if every primary point is Japanese the fit is
  // the whole list.
  const jpCoords = primary.filter((p) => inBounds(JAPAN, p.lng, p.lat)).map((p) => [p.lng, p.lat]);
  const coords = jpCoords.length ? jpCoords : primary.map((p) => [p.lng, p.lat]);
  const overview = coords.every((c) => inBounds(OVERVIEW, c[0], c[1])) ? OVERVIEW : null;
  const bounds = new maplibregl.LngLatBounds(coords[0], coords[0]);
  coords.forEach((c) => bounds.extend(c));
  // Card layout is keyed by FRAME width, not viewport: under 600px wide the
  // card docks as a bottom sheet with the 80px thumbnail; a wider frame gets
  // the 300px left panel. This holds the hub, the brands column and the
  // pop-up together.
  const narrow = () => frame.getBoundingClientRect().width < 600;
  // card:'below' pages also demand the phone (full-bleed) page width before
  // the card moves under the frame: the 2-col brands frame is <600px just
  // after 1200 too, and there the card stays docked (desktop unchanged).
  const bleedMq = cfg.cardBelow && window.matchMedia
    ? window.matchMedia('(max-width: 760px)') : null;
  // Wide frames keep the even 56px ring; narrow ones use the decided
  // asymmetric padding so every star sits inside the 440px frame at the overview.
  const fitPadding = () => (narrow() ? { top: 48, bottom: 48, left: 40, right: 40 } : 56);
  // The card docks inside the frame: a 300px left panel on a wide frame, a
  // bottom sheet on a narrow one, so the star lands in the free area. With
  // card:'below' on a narrow frame the card rides under the frame instead, but
  // the padding stays: it just biases the star above the frame's bottom edge.
  const focusPadding = () => (narrow() ? { bottom: 150 } : { left: 320 });
  const fitMax = Math.min(13.5, ceiling);
  // focusMaxZoom (opt-in): the zoom level a PROGRAMMATIC focus move (the
  // group-chip fit, a chip/row/card/star tap through focusPlace) never goes
  // past, so brands taps stop at city level — street tiles without labels
  // lose all context on a phone (James 7 Oct). Unset: null, and every focus
  // below falls back to its exact old max. The min is against the per-call
  // fallback, which already carries the ceiling, so neither maxZoom (the map's
  // own limit — pinch/scroll still zoom as far as ever) nor the caller's bound
  // is ever crossed; the overview fit (fitMax) and the cluster expansion
  // (expandCluster) never consult this.
  const focusMax = (fallback) => (cfg.focusMaxZoom != null
    ? Math.min(cfg.focusMaxZoom, fallback) : fallback);

  let cardEl = null;    // the docked panel, created once, hidden when closed
  let cardId = -1;      // pid whose card is open
  let listOpen = false; // the card is showing its list mode
  let listGroup = null; // null: no list context; string: the group whose list (or the back control) is available ('' = unclusterable cluster)
  let listMembers = null; // the unclusterable cluster's own leaves, so Back restores that list (not every point)
  let hovered = null;

  const map = new maplibregl.Map({
    container: frame,
    style: buildStyle(),
    center: cfg.initialCenter || [(128.9 + 140.6) / 2, (33.2 + 36.4) / 2],
    zoom: cfg.initialZoom != null ? cfg.initialZoom : 5.4,
    attributionControl: false,
    cooperativeGestures: true,
    dragRotate: false,
    touchPitch: false,
    pitchWithRotate: false,
    hash: false,
    maxBounds: [[mbW, mbS], [mbE, mbN]],
    minZoom: 3.5,
    maxZoom: ceiling,
    renderWorldCopies: false,
    fadeDuration: reduced ? 0 : 220,
    // zh-TW (or null): MapLibre merges these over its own English defaults.
    locale: cfg.mlLocale,
  });

  frame.knsMap = map; // the smoke test counts rendered stars through this handle

  // Outage fallback: if a source or tile fails BEFORE the first load, or the
  // load never arrives, swap the frame for the quiet failure line once and tear
  // the map down. After load a single tile error must not touch anything, so
  // both triggers are gated on `loaded`.
  let loaded = false;
  let failedOut = false;
  const failOnce = () => {
    if (loaded || failedOut) return;
    failedOut = true;
    clearTimeout(outageTimer);
    // Tear the map down off-event (an error can arrive mid event-dispatch),
    // and BEFORE touching the frame's DOM so no render frame runs against a
    // wiped canvas. showFailure then fills the emptied frame with the line.
    setTimeout(() => {
      try { map.remove(); } catch (err) { /* already gone */ }
      showFailure(root, cfg);
    }, 0);
  };
  map.on('error', () => { if (!loaded) failOnce(); });
  // 12 s: past a slow-but-live first load (the smoke gives the map 20 s), yet
  // inside the 15 s the smoke gives a blocked run to show the fallback.
  const outageTimer = setTimeout(failOnce, 12000);

  map.addControl(new maplibregl.AttributionControl({ compact: true }));
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.className = 'kns-map__all';
  allBtn.textContent = cfg.str.all_places;
  allBtn.hidden = true;
  frame.appendChild(allBtn);
  allBtn.addEventListener('click', () => {
    closeCard();
    listGroup = null;
    setActiveChip(null);
    map.fitBounds(overview ? OVERVIEW : bounds, { padding: fitPadding(), maxZoom: fitMax, duration: reduced ? 0 : 800 });
  });

  function syncAllBtn() {
    if (failedOut) return; // the map was torn down by the outage watcher
    const zoomedPast = map.getZoom() > (overview ? 6.4 : 7.2);
    allBtn.hidden = !(zoomedPast || cardId >= 0 || listOpen);
  }
  map.on('move', syncAllBtn);

  function closeCard() {
    if (cardId >= 0) {
      const p = places[cardId];
      map.setFeatureState({ source: srcOf(p), id: cardId }, { selected: false });
    }
    cardId = -1;
    listOpen = false;
    if (cardEl) {
      cardEl.classList.remove('kns-map__card--list');
      cardEl.hidden = true;
    }
    syncAllBtn();
  }

  function closeHead() {
    // The card close button, rebuilt whenever the card body is swapped.
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'kns-map__card-close';
    close.setAttribute('aria-label', cfg.str.close);
    close.textContent = '×';
    close.addEventListener('click', closeCard);
    return close;
  }

  function ensureCard() {
    if (cardEl) return cardEl;
    cardEl = document.createElement('div');
    cardEl.className = 'kns-map__card';
    cardEl.hidden = true;
    frame.appendChild(cardEl); // the default docked home; layoutCard() may move it
    return cardEl;
  }

  // The card sits "under" (after the frame, in flow) exactly when it is
  // mounted there; docked cards live inside the frame, as always.
  const cardUnder = () => !!cardEl && cardEl.parentNode !== frame;

  /* With card:'below' the card rides UNDER the frame exactly when the frame is
     in its bottom-sheet layout (frame width < 600px, the narrow() rule) AND
     the page is narrow enough for the brands' full-bleed aside layout (the
     same 760px line kns-landing-products styles the breakout at — the 2-col
     desktop frame can also sit under 600px, and there the card must stay
     docked so desktop is untouched). Runs on every card open and on resize
     (a rotate past 600px moves it back inside). Pages without the option
     (the hub, the pop-up) never enter here. */
  function layoutCard() {
    if (!cardEl || !cfg.cardBelow) return;
    const wantUnder = narrow() && (!bleedMq || bleedMq.matches);
    if (cardUnder() === wantUnder) return;
    cardEl.classList.toggle('kns-map__card--under', wantUnder);
    cardEl.classList.toggle('is-panel', !wantUnder); // an open card carried across the split keeps its wide-frame look
    frame.classList.toggle('has-panel-card', !wantUnder);
    if (wantUnder) root.insertBefore(cardEl, frame.nextSibling);
    else frame.appendChild(cardEl);
  }

  // A freshly opened card under the map whose top sits off the bottom of the
  // screen gets pulled in; block 'nearest' no-ops when it is already visible.
  // (Card opens only follow a tap, so this never scrolls on its own.)
  function scrollUnderCard() {
    if (cardUnder() && !cardEl.hidden) {
      cardEl.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  function setActiveChip(g) {
    root.querySelectorAll('.kns-map__chips .kns-map__chip').forEach((b) => {
      b.classList.toggle('is-active', groupsOn ? (b.dataset.knsMapGroup || '') === (g || '') : false);
    });
  }

  // The "Back to makers" chevron: restores the list the point came from. The
  // label follows the chip mode: group chips restore a region ("Back to
  // makers"), a whole-set list (an unclusterable cluster) says "All places".
  function makeBack() {
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'kns-map__back';
    back.textContent = groupsOn ? cfg.str.back : cfg.str.all_places;
    back.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (listGroup === null) return;
      openList(listGroup, listMembers); // restore the list; the camera stays put
    });
    return back;
  }

  /* Card body of one point: the image first, the logo only as a fallback
     inside a neutral tile; eyebrow (place, visited), name, endonym, then the
     one link or the links list (a maker card carries two). */
  function cardBody(p) {
    const media = document.createElement('div');
    media.className = 'kns-map__card-media';
    if (p.img) {
      const thumb = document.createElement('img');
      thumb.className = 'kns-map__card-thumb';
      thumb.src = p.img;
      thumb.alt = p.img_alt || p.name;
      thumb.loading = 'lazy';
      thumb.decoding = 'async';
      media.appendChild(thumb);
    } else if (p.logo) {
      const tile = document.createElement('div');
      tile.className = 'kns-map__card-tile';
      const logo = document.createElement('img');
      logo.className = 'kns-map__card-logo';
      logo.src = p.logo;
      logo.alt = p.name;
      logo.loading = 'lazy';
      logo.decoding = 'async';
      tile.appendChild(logo);
      media.appendChild(tile);
    }
    return [media, buildBody(p)];
  }

  function buildBody(p) {
    const visited = visitedText(cfg.str, p.date_iso);
    const body = document.createElement('div');
    body.className = 'kns-map__card-body';
    const eye = document.createElement('p');
    eye.className = 'kns-map__card-eyebrow';
    eye.textContent = (p.eyebrow_place || '') + (p.eyebrow_place && visited ? ' · ' : '') + visited;
    if (!eye.textContent) eye.remove(); else body.appendChild(eye);
    const h = document.createElement('p');
    h.className = 'kns-map__card-name';
    h.textContent = p.name;
    body.appendChild(h);
    if (p.sub) {
      const sub = document.createElement('p');
      sub.className = 'kns-map__card-sub';
      sub.setAttribute('lang', cfg.subLang);
      sub.textContent = p.sub;
      body.appendChild(sub);
    }
    if (Array.isArray(p.links) && p.links.length) {
      const links = document.createElement('div');
      links.className = 'kns-map__card-links';
      p.links.forEach((l) => {
        const a = document.createElement('a');
        a.className = 'kns-map__card-link';
        a.href = l.url;
        a.textContent = l.label;
        if (l.shop) {
          a.addEventListener('click', (ev) => {
            const grid = document.querySelector('.lp-products');
            if (!grid) return;
            const r = grid.getBoundingClientRect();
            // The target is on THIS page just below: scroll, and let the URL
            // change without the reload. Anywhere else, follow the link.
            if (r.bottom > 0 && r.top < window.innerHeight * 3) {
              ev.preventDefault();
              grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
              history.replaceState(null, '', l.url);
              // Apply the maker filter too (cold check 7 Oct: the link scrolled but the grid stayed unfiltered).
              const api = window.knsLandingFilters;
              const mk = new URL(l.url, window.location.href).searchParams.get('maker');
              if (api && mk) { api.clearFilter('maker'); api.setFilter('maker', mk); }
            }
          });
        }
        links.appendChild(a);
      });
      body.appendChild(links);
    } else {
      const link = document.createElement('a');
      link.className = 'kns-map__card-link';
      link.href = p.url;
      link.textContent = p.link_label || cfg.str.read_story;
      body.appendChild(link);
    }
    return body;
  }

  function openCard(p) {
    const card = ensureCard();
    card.textContent = '';
    card.appendChild(closeHead());
    const parts = p.secondary ? [buildBody(p)] : cardBody(p);
    // A card opened out of a list (a row tap, a single-maker group chip) rides
    // the "Back to makers" control at the top of its body, so the list is one
    // tap away.
    if (listGroup !== null) {
      const bodyEl = parts[parts.length - 1];
      bodyEl.insertBefore(makeBack(), bodyEl.firstChild);
    }
    parts.forEach((x) => card.appendChild(x));
    card.classList.remove('kns-map__card--list');
    // Card layout keyed by FRAME width: the is-panel class flips the docked
    // card from the default bottom sheet to the 300px left panel.
    card.classList.toggle('is-panel', !narrow());
    frame.classList.toggle('has-panel-card', !narrow());
    listOpen = false;
    cardId = p.id;
    // The attribution wraps to two lines on narrow screens; the mobile sheet sits above its real height.
    const attrib = frame.querySelector('.maplibregl-ctrl-bottom-right');
    frame.style.setProperty('--kns-map-attrib-h', (attrib ? attrib.offsetHeight : 26) + 'px');
    // Under-mount (card:'below'): place before the reveal, then a freshly
    // opened one below the fold is scrolled in.
    layoutCard();
    card.hidden = false;
    scrollUnderCard();
    syncAllBtn();
  }

  /* List card mode: an eyebrow ("SAKAI · 7 MAKERS"), a "Back to makers"
     control, then rows of at least 44px (40px thumbnail, name, Japanese name).
     Tapping a row swaps to that point's card. No spiderfy. */
  function openList(group, leaves) {
    const card = ensureCard();
    listOpen = true;
    listGroup = group === undefined ? null : group;
    listMembers = leaves || null;
    // An unclusterable cluster lists ITS OWN leaves (7 Oct: listing every point
    // put Anryu Hamono and CCK under a Sakai cluster); the whole set is only the
    // fallback when the leaves cannot be read.
    const members = leaves || (group ? primary.filter((p) => p.group === group) : primary);
    card.textContent = '';
    card.appendChild(closeHead());
    const body = document.createElement('div');
    body.className = 'kns-map__card-body';
    const eye = document.createElement('p');
    eye.className = 'kns-map__card-eyebrow';
    let n = 0;
    members.forEach(() => { n++; });
    eye.textContent = (group ? group + ' · ' : '') + cfg.str.makers_of.replace('{n}', String(n));
    body.appendChild(eye);
    body.appendChild(makeBack());
    const rows = document.createElement('div');
    rows.className = 'kns-map__rows';
    members.forEach((p) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'kns-map__row';
      if (p.img) {
        const im = document.createElement('img');
        im.src = p.img;
        im.alt = '';
        im.loading = 'lazy';
        im.decoding = 'async';
        row.appendChild(im);
      } else {
        const tile = document.createElement('span');
        tile.className = 'kns-map__row-tile';
        row.appendChild(tile);
      }
      const names = document.createElement('span');
      names.className = 'kns-map__row-names';
      const nm = document.createElement('span');
      nm.className = 'kns-map__row-name';
      nm.textContent = p.name;
      names.appendChild(nm);
      if (p.sub) {
        const sub = document.createElement('span');
        sub.className = 'kns-map__row-sub';
        sub.setAttribute('lang', cfg.subLang);
        sub.textContent = p.sub;
        names.appendChild(sub);
      }
      row.appendChild(names);
      row.addEventListener('click', (ev) => {
        ev.stopPropagation();
        focusPlace(p, true); // keep the list context so the card can go back
      });
      rows.appendChild(row);
    });
    body.appendChild(rows);
    card.appendChild(body);
    card.classList.add('kns-map__card--list');
    card.classList.toggle('is-panel', !narrow());
    frame.classList.toggle('has-panel-card', !narrow());
    layoutCard(); // under-mount (card:'below') before reveal, as in openCard
    card.hidden = false;
    scrollUnderCard();
    syncAllBtn();
  }

  // A group chip fits the group (allowing for the docked card) AND opens the
  // list card; a group of one opens that point's card instead (decided).
  function fitGroup(g, openTheList) {
    const members = primary.filter((p) => p.group === g);
    let n = 0;
    members.forEach(() => { n++; });
    if (!members.length) return;
    if (n === 1) {
      focusPlace(members[0]);
      return;
    }
    const gb = new maplibregl.LngLatBounds([members[0].lng, members[0].lat], [members[0].lng, members[0].lat]);
    members.forEach((p) => gb.extend([p.lng, p.lat]));
    // maxZoom here is a fit bound, not the map's own limit: focusMaxZoom (set)
    // stops the group fit at city level, else the old Math.min(fitMax, ceiling).
    map.fitBounds(gb, { padding: focusPadding(), maxZoom: focusMax(Math.min(fitMax, ceiling)), duration: reduced ? 0 : 750 });
    if (openTheList) openList(g);
  }

  // One camera move + card for a point. Declared at init scope (hoisted): the
  // list rows and the group chips call it, and it runs against the map after
  // load has wired the sources.
  function focusPlace(p) {
    closeCard();           // clears the old selected state; the camera does not move on close
    if (hovered === p.id) {
      map.removeFeatureState({ source: srcOf(p), id: p.id }, 'hover');
      hovered = null;
    }
    if (groupsOn) {
      setActiveChip(p.group || null);
    } else {
      root.querySelectorAll('.kns-map__chips .kns-map__chip').forEach((b) => {
        b.classList.toggle('is-active', Number(b.dataset.knsMapPid) === p.id);
      });
      const activeChip = root.querySelector('.kns-map__chips .kns-map__chip[data-kns-map-pid="' + p.id + '"]');
      if (activeChip && activeChip.scrollIntoView) {
        activeChip.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    }
    map.setFeatureState({ source: srcOf(p), id: p.id }, { selected: true });
    openCard(p); // the card shows at once, on the same click (Astra, kept over the settle wait)
    // One camera move to zoom 14.5 (or focusMaxZoom, when set) at the default
    // speed, never past maxZoom (a capped modal stays at town level); the
    // padding keeps the star out from under the docked card. MapLibre jumps
    // for reduced-motion users.
    map.flyTo({
      center: [p.lng, p.lat],
      zoom: focusMax(Math.min(14.5, ceiling)),
      padding: focusPadding(),
      maxDuration: reduced ? 0 : 2500,
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCard();
  });

  // card:'below' pages only (the hub and the pop-up add nothing): a rotate or
  // a resize that crosses 600px (or the 760px line) moves an open card between
  // the two homes. layoutCard is a cheap no-op when the side did not change.
  if (cfg.cardBelow) {
    window.addEventListener('resize', layoutCard);
  }

  map.on('load', () => {
    if (failedOut) return; // the timeout already showed the fallback; leave it shown
    loaded = true;
    clearTimeout(outageTimer);
    map.addImage('kns-map-star', starCanvas(96), { sdf: true, pixelRatio: 2 });

    map.addSource('kns-map-points', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: primary.map((p) => ({
          type: 'Feature',
          properties: { pid: p.id },
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        })),
      },
      cluster: true,
      clusterRadius: 40,
      clusterMaxZoom: clusterMaxZoom,
      promoteId: 'pid',
    });
    if (secondary.length) {
      // The pop-up's neighbours: their own unclustered source, so they can
      // never collapse into a gold-ringed cluster.
      map.addSource('kns-map-secondary', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: secondary.map((p) => ({
            type: 'Feature',
            properties: { pid: p.id },
            geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
          })),
        },
        promoteId: 'pid',
      });
    }

    // Quiet persistent glow under every gold star; brighter and wider when selected.
    map.addLayer({
      id: 'kns-map-glow',
      type: 'circle',
      source: 'kns-map-points',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          5, ['case', ['boolean', ['feature-state', 'selected'], false], 20, 14],
          13, ['case', ['boolean', ['feature-state', 'selected'], false], 28, 14],
        ],
        'circle-color': GOLD,
        'circle-blur': 0.75,
        'circle-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 0.35, 0.15],
      },
    });

    map.addLayer({
      id: 'kns-map-cluster',
      type: 'circle',
      source: 'kns-map-points',
      filter: ['has', 'point_count'],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 10, 11, 15],
        'circle-color': LAND, // dark disc, gold ring, gold count
        'circle-stroke-color': GOLD,
        'circle-stroke-width': 1.5,
      },
    });
    map.addLayer({
      id: 'kns-map-cluster-count',
      type: 'symbol',
      source: 'kns-map-points',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': LABEL_FONT,
        'text-size': 11,
      },
      paint: { 'text-color': GOLD },
    });

    map.addLayer({
      id: 'kns-map-star',
      type: 'symbol',
      source: 'kns-map-points',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'kns-map-star',
        // Layout properties cannot read feature-state in MapLibre, so size is
        // zoom-only here; the selected star draws larger via its own layer below.
        'icon-size': ['interpolate', ['linear'], ['zoom'],
          5, 20 / STAR_BASE,
          13, 24 / STAR_BASE,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': false, // stars hold their box; labels step aside
      },
      paint: {
        'icon-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 0, 1],
        // The selected star lives in kns-map-star-selected, so hover must not brighten it.
        'icon-color': ['case',
          ['boolean', ['feature-state', 'selected'], false], GOLD,
          ['boolean', ['feature-state', 'hover'], false], GOLD_BRIGHT,
          GOLD],
        'icon-halo-color': 'rgba(15,15,17,0.9)',
        'icon-halo-width': 1.4,
      },
    });

    // The selected star draws about 28px on top (layout cannot read
    // feature-state, so the swap rides icon-opacity on both layers).
    map.addLayer({
      id: 'kns-map-star-selected',
      type: 'symbol',
      source: 'kns-map-points',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'kns-map-star',
        'icon-size': 28 / STAR_BASE,
        'icon-allow-overlap': true,
        'icon-ignore-placement': false,
      },
      paint: {
        'icon-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0],
        'icon-color': GOLD_BRIGHT,
        'icon-halo-color': 'rgba(18,14,0,0.95)',
        'icon-halo-width': 1.4,
      },
    });

    if (secondary.length) {
      // Secondary makers: a 6px dim grey dot, no glow.
      map.addLayer({
        id: 'kns-map-secondary',
        type: 'circle',
        source: 'kns-map-secondary',
        paint: {
          'circle-radius': ['case',
            ['boolean', ['feature-state', 'selected'], false], 7,
            ['boolean', ['feature-state', 'hover'], false], 6.5, 6],
          'circle-color': ['case',
            ['boolean', ['feature-state', 'hover'], false], GREY_BRIGHT, GREY_DOT],
          'circle-opacity': ['case',
            ['boolean', ['feature-state', 'selected'], false], 1,
            ['boolean', ['feature-state', 'hover'], false], 0.95, 0.65],
          'circle-stroke-width': ['case', ['boolean', ['feature-state', 'selected'], false], 1.5, 0],
          'circle-stroke-color': GOLD,
        },
      });
      // The name, only while hovered or selected.
      map.addLayer({
        id: 'kns-map-secondary-label',
        type: 'symbol',
        source: 'kns-map-secondary',
        filter: ['==', ['boolean', ['feature-state', 'hover'], false], true],
        layout: {
          'text-field': ['get', 'name'],
          'text-font': LABEL_FONT,
          'text-size': 11,
          'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
          'text-radial-offset': 0.9,
          'text-optional': true,
        },
        paint: {
          'text-color': '#d7d8dc',
          'text-halo-color': 'rgba(18,18,20,0.9)',
          'text-halo-width': 1.4,
        },
      });
      map.addLayer({
        id: 'kns-map-secondary-hit',
        type: 'circle',
        source: 'kns-map-secondary',
        paint: { 'circle-radius': 16, 'circle-opacity': 0, 'circle-color': '#ffffff' },
      });
    }

    // Invisible 48px-wide target so every star has a hit area of at least 44px.
    map.addLayer({
      id: 'kns-map-star-hit',
      type: 'circle',
      source: 'kns-map-points',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 22,
        'circle-opacity': 0,
        'circle-color': '#ffffff',
      },
    });

    // Hover ring on top of the star (same click/hover feature set).
    map.addLayer({
      id: 'kns-map-star-ring',
      type: 'circle',
      source: 'kns-map-points',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 13,
        'circle-opacity': 0,
        'circle-stroke-width': 1,
        'circle-stroke-color': GOLD,
        'circle-stroke-opacity': ['case',
          ['boolean', ['feature-state', 'hover'], false], 0.95,
          ['boolean', ['feature-state', 'selected'], false], 0.95,
          0],
      },
    });

    let introDone = cfg.initialCenter != null; // a given center means no fly-in
    const fitPlaces = (ms) => {
      map.fitBounds(bounds, { padding: fitPadding(), maxZoom: fitMax, duration: reduced ? 0 : ms });
    };
    const startIntro = () => {
      if (introDone) return;
      introDone = true;
      if (reduced) { fitPlaces(0); return; }
      const start = map.cameraForBounds(overview ? OVERVIEW : JAPAN, { padding: 20, maxZoom: 5.6 });
      const dest = map.cameraForBounds(bounds, { padding: fitPadding(), maxZoom: fitMax });
      if (start && dest && start.center != null && dest.center != null) {
        map.jumpTo({ center: start.center, zoom: Math.min(start.zoom, 5.6) });
        setTimeout(() => map.flyTo({
          center: dest.center,
          zoom: dest.zoom,
          curve: 1.2,
          duration: 1600,
        }), 120);
      } else {
        fitPlaces(600);
      }
    };
    if (cfg.initialCenter == null) map.fitBounds(JAPAN, { padding: 10, maxZoom: 5.6, duration: 0 });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); startIntro(); }
      }, { rootMargin: '200px 0px' });
      io.observe(root);
    } else {
      startIntro();
    }

    // What sits under a screen point: gold stars first (the hit ring, then the
    // painted symbol itself), then clusters, then secondary dots. The dense
    // overview packs hit rings on top of each other, and queryRenderedFeatures
    // returns them in no useful order, so among the stars under the point the
    // one whose centre sits closest to the tap wins (the tapped star is 0px
    // away; a neighbour's ring covering the same pixel is 20+).
    function nearest(features, pt) {
      let best = features[0];
      let bd = Infinity;
      features.forEach((f) => {
        const g = f.geometry && f.geometry.coordinates;
        if (!g || !Number.isFinite(g[0])) return;
        const q = map.project(g);
        const d = Math.hypot(q.x - pt.x, q.y - pt.y);
        if (d < bd) { bd = d; best = f; }
      });
      return best;
    }
    function pick(pt) {
      const stars = map.queryRenderedFeatures(pt, { layers: ['kns-map-star-hit', 'kns-map-star'] });
      if (stars.length) return { p: nearest(stars, pt) };
      const clusters = map.queryRenderedFeatures(pt, { layers: ['kns-map-cluster'] });
      if (clusters.length) return { c: clusters[0] };
      if (secondary.length) {
        const dots = map.queryRenderedFeatures(pt, { layers: ['kns-map-secondary-hit', 'kns-map-secondary'] });
        if (dots.length) return { p: nearest(dots, pt) };
      }
      return null;
    }
    const pointAt = (f) => { const id = Number(f.id); return Number.isFinite(id) && id < places.length ? places[id] : null; };

    map.on('mousemove', (e) => {
      const hit = pick(e.point);
      const f = hit && hit.p ? hit.p : null;
      if (hovered !== null && (!f || f.id !== hovered)) {  // id 0 is a real place
        // key is the 2nd arg; keeps 'selected'
        map.removeFeatureState({ source: srcOf(places[hovered]), id: hovered }, 'hover');
        hovered = null;
      }
      if (f) {
        hovered = f.id;
        const p = pointAt(f);
        map.setFeatureState({ source: srcOf(p), id: f.id }, { hover: true });
        map.getCanvas().style.cursor = 'pointer';
      } else {
        map.getCanvas().style.cursor = '';
      }
    });

    function expandCluster(f) {
      const cid = f.properties && f.properties.cluster_id != null
        ? Number(f.properties.cluster_id)
        : Number(f.id);
      // In MapLibre 6.x this lives on the source, not the map.
      const src = map.getSource('kns-map-points');
      const zoomP = src && src.getClusterExpansionZoom
        ? Promise.resolve(src.getClusterExpansionZoom(cid))
        : Promise.reject(new Error('no source'));
      zoomP.then((zoom) => {
        map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(zoom, ceiling), duration: reduced ? 0 : 650 });
      }).catch(() => {
        map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(map.getZoom() + 1.5, ceiling), duration: reduced ? 0 : 650 });
      });
    }

    // A star resolves through the hit layer first and the star's own symbol
    // layer second. The symbol geometry is the painted star itself, so a tap
    // lands even where the invisible circle query comes back empty: at the
    // 320px overview (zoom ~4.2, a third of the desktop one) dense tiles can
    // report no hit circle at a visible star's own point. A cluster expands
    // while expansion is possible; once it cannot expand (its expansion zoom
    // sits at the ceiling, so the points are within a few pixels of each
    // other) it opens the list card instead. No spiderfy, decided.
    map.on('click', (e) => {
      const hit = pick(e.point);
      if (hit && hit.p) {
        const p = pointAt(hit.p);
        if (p) focusPlace(p);
        return;
      }
      if (hit && hit.c) {
        const done = (stuck) => {
          if (!stuck) { expandCluster(hit.c); return; }
          closeCard();
          const ls = map.getSource('kns-map-points');
          const lid = hit.c.properties.cluster_id;
          const all = () => openList('');
          if (!ls || !ls.getClusterLeaves || lid == null) { all(); return; }
          Promise.resolve(ls.getClusterLeaves(Number(lid), 500, 0)).then((fs) => {
            const ms = (fs || []).map(pointAt).filter(Boolean);
            if (ms.length) openList('', ms); else all();
          }).catch(all);
        };
        if (map.getZoom() >= clusterMaxZoom) { done(true); return; }
        const src = map.getSource('kns-map-points');
        const cid = hit.c.properties.cluster_id;
        if (src && src.getClusterExpansionZoom && cid != null) {
          src.getClusterExpansionZoom(Number(cid)).then((z) => {
            done(Math.min(z, ceiling) >= Math.min(clusterMaxZoom, ceiling));
          }).catch(() => done(false));
        } else done(false);
      }
    });

    if (chipsBox) {
      chipsBox.querySelectorAll('.kns-map__chip').forEach((btn) => {
        btn.addEventListener('click', () => {
          // A chip click always leaves any list context: the card it opens is a
          // fresh start, never a return to a stale group's list.
          listGroup = null;
          if (groupsOn) {
            const g = btn.dataset.knsMapGroup || '';
            setActiveChip(g || null);
            if (!g) {
              closeCard();
              listGroup = null;
              map.fitBounds(overview ? OVERVIEW : bounds, { padding: fitPadding(), maxZoom: fitMax, duration: reduced ? 0 : 800 });
              return;
            }
            fitGroup(g, true); // fit + list; a group of one opens the point card
          } else {
            const p = places[Number(btn.dataset.knsMapPid)];
            if (p) focusPlace(p);
          }
        });
      });
    }

    syncAllBtn();
  });

  return true;
}

const bootedRoots = new Set();

function bootRoot(root) {
  if (bootedRoots.has(root)) return;
  bootedRoots.add(root);
  const cfg = readConfig(root);
  if (!cfg) { showFailure(root, null); return; }
  const start = () => {
    if (start.running) return;
    start.running = true;
    Promise.all([
      loadCss(),
      import(ML_URL),
      // Lazy extra points (the pop-up's neighbours) ride the boot: the fetch
      // starts with MapLibre, never before the root is near the viewport or,
      // in mode 'open', the pill opens the modal. A failed fetch keeps the
      // given points.
      cfg.pointsUrl
        ? fetch(cfg.pointsUrl).then((r) => (r && r.ok ? r.json() : null))
          .then((j) => { cfg.extra = cleanPoints(Array.isArray(j) ? j : (j && j.points), 100); })
          .catch(() => { cfg.extra = null; })
        : Promise.resolve(),
    ]).then(([, ml]) => {
      if (cfg.extra && cfg.extra.length) {
        // Fetched points take ids after the inline ones; an inline point the
        // fetch repeats (the focal maker) keeps its inline entry. With
        // secondaryFrom set (the pop-up: the focal maker's url), everything the
        // fetch adds dims to a grey dot; unset (the brands page, which today
        // fetches nothing at all) they would stay full stars. A root with
        // NO inline points at all (the geo-less maker's pop-up) gets the
        // fetched set as its whole map: the empty-points verdict is deferred
        // to after this merge, so it boots at the Japan overview, no focal
        // star. Only a failed fetch WITH no inline points reaches init as null
        // (the one case that shows the fallback).
        cfg.points = cfg.points || [];
        const seen = new Set(cfg.points.map((p) => p.url));
        cfg.extra.forEach((p) => {
          if (!seen.has(p.url)) {
            cfg.points.push(Object.assign({}, p, { id: cfg.points.length },
              cfg.secondaryFrom ? { secondary: true } : null));
          }
        });
      }
      if (!init(root, ml.default || ml, cfg)) showFailure(root, cfg);
    }).catch(() => {
      start.running = false;
      showFailure(root, cfg);
    });
  };
  // Disclosure rows (brands mobile): the label carries the maker count, and
  // opening starts the boot, so MapLibre is never fetched while collapsed.
  // The row exists in the markup on every width but shows only on phones
  // (CSS); while it is visible it OWNS the boot, so the idle-root observer
  // must not start one behind the closed row.
  const disc = root.querySelector('.kns-map__disclosure');
  if (disc) {
    let n = 0;
    if (cfg.points) cfg.points.forEach(() => { n++; });
    const lab = disc.querySelector('.kns-map__disclosure-label');
    if (lab) lab.textContent = cfg.str.disclosure.replace('{n}', String(n));
    disc.addEventListener('click', () => {
      const open = root.classList.toggle('is-open');
      disc.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) start();
    });
  }
  const discOwns = disc && window.getComputedStyle
    && window.getComputedStyle(disc).display !== 'none';
  if (disc || cfg.mode === 'open') root.knsMapBoot = start;
  if (cfg.mode === 'open' || discOwns) {
    // A modal root, or a phone-width brands row: nothing (not even MapLibre)
    // loads until the pill or the disclosure asks. On desktop the disclosure is
    // display:none, so the observer below still boots the visible map.
    return;
  }
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
    }, { rootMargin: '400px 0px' });
    io.observe(root);
  } else {
    start();
  }
}

function boot() {
  document.querySelectorAll('.kns-map').forEach(bootRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
